import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { img } from "@/lib/api";

const useReducedMotion = () => {
  const [reduced, setReduced] = useState(() => window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReduced(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return reduced;
};

const Slide = ({ s, i, active, reduced }) => (
  <div className="relative min-w-0 flex-[0_0_100%] overflow-hidden" role="group" aria-roledescription="slide" aria-label={`${i + 1}`} data-testid={`hero-slide-${i}`}>
    <picture>
      {s.image_mobile && <source media="(max-width: 767px)" srcSet={img(s.image_mobile)} />}
      <img src={img(s.image)} alt={s.title} loading="eager" fetchPriority={i === 0 ? "high" : "low"} decoding="async"
        style={{ "--pd": s.position || "center", "--pm": s.position_mobile || "center top" }}
        className={`absolute inset-0 h-full w-full object-cover [object-position:var(--pm)] md:[object-position:var(--pd)] ${reduced ? "" : "transition-transform duration-[6000ms] ease-out"} ${active && !reduced ? "scale-105" : "scale-100"}`} />
    </picture>
    <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent md:bg-gradient-to-r md:from-black/60 md:via-black/20 md:to-transparent" />
    <div className="relative mx-auto flex h-full max-w-7xl items-end px-5 pb-20 sm:px-6 md:items-center md:pb-0">
      {active && (
        <div key={s.id} className="max-w-xl text-white">
          <h2 className="lt-rise font-display text-4xl font-bold leading-[1.05] tracking-[-0.03em] [text-shadow:0_2px_24px_rgba(0,0,0,0.35)] sm:text-5xl lg:text-6xl" data-testid={`hero-slide-title-${i}`}>{s.title}</h2>
          {s.subtitle && <p className="lt-rise mt-4 text-base text-white/90 [animation-delay:120ms] md:text-lg">{s.subtitle}</p>}
          {s.button && s.link && (
            <Link to={s.link} data-testid={`hero-slide-cta-${i}`}
              className="lt-rise mt-8 inline-flex h-13 items-center gap-2 rounded-full bg-[#0066FF] px-7 py-3.5 font-semibold text-white shadow-[0_12px_30px_-8px_rgba(0,102,255,0.7)] transition-[background-color,transform] [animation-delay:220ms] hover:-translate-y-0.5 hover:bg-[#1a75ff]">
              {s.button} <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </div>
      )}
    </div>
  </div>
);

export const HeroSlider = ({ slides }) => {
  const reduced = useReducedMotion();
  const [ref, embla] = useEmblaCarousel({ loop: true, duration: reduced ? 1 : 34 });
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (!embla) return undefined;
    const sel = () => setIndex(embla.selectedScrollSnap());
    const down = () => setPaused(true);
    const up = () => setPaused(false);
    embla.on("select", sel).on("pointerDown", down).on("pointerUp", up);
    sel();
    return () => {
      embla.off("select", sel).off("pointerDown", down).off("pointerUp", up);
    };
  }, [embla]);

  useEffect(() => {
    if (!embla || paused || reduced || (slides?.length || 0) < 2) return undefined;
    const t = setTimeout(() => embla.scrollNext(), 5000);
    return () => clearTimeout(t);
  }, [embla, paused, reduced, slides, index]);

  const go = useCallback((fn) => () => embla && fn(embla), [embla]);
  if (!slides) return <div className="h-[82svh] max-h-[760px] min-h-[520px] animate-pulse bg-slate-100" />;
  if (!slides.length) return null;

  return (
    <section className="relative" aria-roledescription="carousel" aria-label="Novità LeonTech" data-testid="hero-slider"
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)}>
      <div ref={ref} className="h-[82svh] max-h-[760px] min-h-[520px] overflow-hidden">
        <div className="flex h-full touch-pan-y">
          {slides.map((s, i) => <Slide key={s.id} s={s} i={i} active={i === index} reduced={reduced} />)}
        </div>
      </div>
      {slides.length > 1 && (
        <>
          <button onClick={go((e) => e.scrollPrev())} aria-label="Slide precedente" data-testid="hero-prev-btn"
            className="absolute left-4 top-1/2 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/85 text-slate-900 shadow-lg backdrop-blur transition-[background-color,transform] hover:scale-105 hover:bg-white md:flex">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button onClick={go((e) => e.scrollNext())} aria-label="Slide successiva" data-testid="hero-next-btn"
            className="absolute right-4 top-1/2 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/85 text-slate-900 shadow-lg backdrop-blur transition-[background-color,transform] hover:scale-105 hover:bg-white md:flex">
            <ChevronRight className="h-5 w-5" />
          </button>
          <div className="absolute bottom-7 left-1/2 flex -translate-x-1/2 gap-2" data-testid="hero-dots">
            {slides.map((s, i) => (
              <button key={s.id} onClick={go((e) => e.scrollTo(i))} aria-label={`Vai alla slide ${i + 1}`} aria-current={i === index} data-testid={`hero-dot-${i}`}
                className={`h-2 rounded-full transition-[width,background-color] duration-300 ${i === index ? "w-8 bg-white" : "w-2 bg-white/55 hover:bg-white/80"}`} />
            ))}
          </div>
        </>
      )}
    </section>
  );
};
